import React, { useState } from 'react';
import { Content, NavigationConfiguration } from './apiService';
import CardBadge from './CardBadge';
import CountryYear from './CountryYear';
import { Box, Card, CardBody, CardImage, Ellipsis, EllipsisText, Flex, Icon, Text } from './styles';
import { durationToWords, getGenreTitles, getNavigationUrl, getWidgetsTeaserPath } from './utils';
import Link from 'next/link';
import styled from '@emotion/styled';
import { IconPlay, IconThumbsUpFilled, IconCainLink } from './icons';
import Mp4Player from './Components/Widgets/WidgetManager/WidgetsPlayer/Mp4Player';
import HlsPlayer from './Components/Widgets/WidgetManager/WidgetsPlayer/LazyHlsPlayer';
import DefaultPreviewButtons from './Components/Widgets/WidgetManager/WidgetsPlayer/DefaultPreviewButtons';
import DesktopOnlyWrapper from './styles/DesktopOnlyWrapper';
import { CardPlaceholder } from './styles/CardPlaceholder';
import useDelayedHover from './useDelayedHover';

interface VitrineCardProps {
  id?: string;
  imagePath?: string;
  content?: Content;
  navigationConfiguration?: NavigationConfiguration;
  title?: string;
  description?: string;
  active?: boolean;
  /** First-row / above-the-fold: skip lazy-loading. */
  eager?: boolean;
  /**
   * First card only. When Lighthouse picks this poster as LCP (slider late / same-size race),
   * it needs fetchpriority=high. Do not put high on the whole row — that fights the slider.
   */
  priority?: boolean;
  onHoverChange?: (id: string | null) => void;
}

export default function VitrineCard({
  id,
  imagePath,
  content,
  navigationConfiguration,
  title: itemTitle,
  description,
  active = false,
  eager = false,
  priority = false,
  onHoverChange,
}: VitrineCardProps) {
  const {
    short_id,
    title: contentTitle,
    slug,
    categories,
    duration,
    flag,
    rate,
  } = content ?? {};
  const cardId = id ?? content?.id;
  const isNonContentNavigation = navigationConfiguration?.navigation_type !== 'content';
  const title = isNonContentNavigation ? itemTitle : contentTitle;
  const showDescription = isNonContentNavigation && !!description;
  const href = (navigationConfiguration ? getNavigationUrl(navigationConfiguration, slug, short_id) : `/contents/${short_id}/${slug}`) ?? '#';
  const durationWords = duration && durationToWords(duration);
  const contentGenres = getGenreTitles(categories, 1);
  const teaserPath = getWidgetsTeaserPath(content?.video_files, content?.id);
  const [hovered, setHovered] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);

  const delayedHover = useDelayedHover((isHovered) => {
    setHovered(isHovered);
    onHoverChange?.(isHovered ? cardId ?? null : null);
  });

  const onMouseEnter = () => {
    delayedHover(true);
  };

  const onMouseLeave = () => {
    delayedHover(false);
  };

  const poster = imagePath ? `${imagePath}?w=300` : undefined;
  const imageLoading = priority || eager ? ('eager' as const) : ('lazy' as const);
  // When this card's image is the LCP (often the placeholder paints first), both imgs need high.
  const fetchPriority = priority ? ('high' as const) : undefined;
  const posterImageProps = {
    src: `${imagePath}?w=300`,
    alt: title,
    loading: imageLoading,
    onLoad: () => setPosterLoaded(true),
    ...(fetchPriority ? ({ fetchpriority: fetchPriority } as any) : {}),
  };
  const placeholderProps = {
    loading: imageLoading,
    fetchPriority,
  };

  return (
    <Link href={href}>
      <a rel="nofollow">
        <MobileCard>
          <Card orientation="horizontal" block>
            {!posterLoaded && <CardPlaceholder {...placeholderProps} />}
            <CardImage {...posterImageProps} />
            {!isNonContentNavigation && <CardBadge flag={flag ?? ''} />}
            <CardBody px={2} py={3}>
              {title && (
                <Text mb={{ md: 1, lg: 2 }} variant="body2" as="h2" weight="demibold">
                  <Ellipsis>{title}</Ellipsis>
                </Text>
              )}
              {showDescription && (
                <Text variant="body3" as="div" mb={{ md: 0, lg: 1 }}>
                  <Ellipsis>{description}</Ellipsis>
                </Text>
              )}
              {!isNonContentNavigation && durationWords && (
                <Text variant="body3" as="div" mb={{ md: 0, lg: 1 }}>
                  {durationWords}
                </Text>
              )}
              {!isNonContentNavigation && (
                <Text variant="body3" as="div">
                  <Ellipsis>
                    <CountryYear mb={1} {...content} textOnly limit={2} />
                    {contentGenres && (
                      <>
                        <Box as="span" px={1}>
                          -
                        </Box>
                        {contentGenres}
                      </>
                    )}
                  </Ellipsis>
                </Text>
              )}
            </CardBody>
          </Card>
        </MobileCard>
        <DesktopOnlyWrapper>
          <CardContainer active={active} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <Card
              orientation="horizontal"
              block
              bottomLeftRadius={false}
              bottomRightRadius={false}
            >
              {!posterLoaded && <CardPlaceholder {...placeholderProps} />}
              <CardImage {...posterImageProps} data-image={hovered && teaserPath ? 'true' : 'false'} />
              {hovered &&
                teaserPath?.src &&
                (teaserPath.type === 'mp4' ? (
                  <Mp4Player src={teaserPath.src} poster={poster} active={active} loop autoplay>
                    {(api) => <DefaultPreviewButtons api={api} />}
                  </Mp4Player>
                ) : (
                  <HlsPlayer source={teaserPath} poster={poster} active={active} loop autoplay>
                    {(api) => <DefaultPreviewButtons api={api} />}
                  </HlsPlayer>
                ))}
              {!isNonContentNavigation && <CardBadge flag={flag ?? ''} data-badge={teaserPath ? 'true' : 'false'} />}
            </Card>
            <PlayButton data-play-button="true">{teaserPath ? <IconPlay /> : <IconCainLink />}</PlayButton>
            <HoverDetails data-hover-details="true">
              <Flex justify="space-between" align="flex-start">
                {title && (
                  <HoverTitle mb={1} variant="body3" as="h2" weight="demibold" line={2}>
                    {title}
                  </HoverTitle>
                )}
                {!isNonContentNavigation && (
                  <HoverRate mb={1} variant="body3" as="div">
                    <Icon size="semiSmall" ml="0.3rem">
                      <IconThumbsUpFilled />
                    </Icon>
                    %{rate}
                  </HoverRate>
                )}
              </Flex>
              {isNonContentNavigation ? (
                showDescription && (
                  <Text variant="body3" as="div" mb={1} color="weak">
                    <Ellipsis>{description}</Ellipsis>
                  </Text>
                )
              ) : (
                <Text variant="body3" as="div" mb={1} color="weak">
                  <Ellipsis>
                    <CountryYear mb={1} {...content} textOnly limit={2} />
                  </Ellipsis>
                </Text>
              )}
            </HoverDetails>
          </CardContainer>
        </DesktopOnlyWrapper>
      </a>
    </Link>
  );
}

const MobileCard = styled.div(() => ({
  display: 'block',
  '@media (min-width: 1200px)': {
    display: 'none',
  },
}));

const CardContainer = styled.div<{ active: boolean }>(({ theme, active }) => ({
  position: 'relative',
  zIndex: 1,
  transition: 'transform 550ms ease',
  transformOrigin: 'center',
  ...(active && {
    transform: 'scale(1.25)',
    zIndex: 9999,
    '& [data-image="true"]': {
      display: 'none',
    },
    '& video': {
      opacity: 1,
    },
    '& [data-hover-details="true"]': {
      opacity: 1,
      transform: 'translateY(0)',
      pointerEvents: 'auto',
    },
    '& [data-play-button="true"]': {
      opacity: 1,
      transform: 'translate(-50%, 0) scale(1)',
    },
    '& [data-badge="true"]': {
      opacity: 0,
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'scale(1.04)',
    },
  }),
  '&:fullscreen [data-play-button="true"]': {
    opacity: 1,
    bottom: theme.spacing(9),
    transform: 'translate(-50%, 8px) scale(.95)',
  },
}));

const PlayButton = styled.div(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  bottom: '-1.75rem',
  width: theme.spacing(7),
  height: theme.spacing(7),
  borderRadius: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  backgroundColor: theme.palette.foreground[800],
  transform: 'translate(-50%, 8px) scale(.95)',
  transformOrigin: 'center',
  willChange: 'transform',
  opacity: 0,
  transition: 'opacity 550ms ease, transform 550ms ease',
  zIndex: 4,
  pointerEvents: 'none',
  '& svg': {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    display: 'block',
    transform: 'translate3d(0,0,0)',
    willChange: 'transform',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 8,
    borderRadius: '50%',
    background: '#b21f45',
    zIndex: -1,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const HoverDetails = styled.div(({ theme }) => ({
  position: 'absolute',
  top: '99%',
  left: 0,
  right: 0,
  background: theme.palette.foreground[800],
  borderBottomLeftRadius: theme.radius.medium,
  borderBottomRightRadius: theme.radius.medium,
  padding: `${theme.spacing(4)} ${theme.spacing(2)} ${theme.spacing(2)}`,
  opacity: 0,
  transform: 'translateY(-8px)',
  transition: 'opacity 550ms ease, transform 550ms ease',
  pointerEvents: 'none',
  zIndex: 3,
}));

const HoverTitle = styled(EllipsisText)({
  flex: 1,
  minWidth: 0,
});

const HoverRate = styled(Text)(({ theme }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginRight: theme.spacing(3),
}));
